import type { JWTToken } from '@/types/branded';
import { createJWTToken } from '@/types/branded';

type TokenRefreshSubscriber = (token: JWTToken | null) => void;

const TOKEN_STORAGE_KEY = 'auth_token';
const STORAGE_TYPE_KEY = 'auth_storage_type';

class TokenManager {
  private static instance: TokenManager;
  private token: JWTToken | null = null;
  private refreshPromise: Promise<JWTToken | null> | null = null;
  private subscribers: TokenRefreshSubscriber[] = [];
  private usePersistentStorage: boolean = true;

  private constructor() {
    this.loadTokenFromStorage();
  }

  static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  private getStorage(): Storage {
    return this.usePersistentStorage ? localStorage : sessionStorage;
  }

  private loadTokenFromStorage(): void {
    // Check storage type preference first
    const storageType = localStorage.getItem(STORAGE_TYPE_KEY);
    this.usePersistentStorage = storageType !== 'session';

    // Try localStorage first, then sessionStorage
    let storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!storedToken) {
      storedToken = sessionStorage.getItem(TOKEN_STORAGE_KEY);
      if (storedToken) {
        this.usePersistentStorage = false;
      }
    }

    if (storedToken) {
      this.token = createJWTToken(storedToken);
    }
  }

  getToken(): JWTToken | null {
    return this.token;
  }

  setToken(token: JWTToken | null, rememberMe: boolean = true): void {
    this.token = token;
    this.usePersistentStorage = rememberMe;

    // Clear from both storages first
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);

    if (token) {
      this.getStorage().setItem(TOKEN_STORAGE_KEY, token);
      localStorage.setItem(STORAGE_TYPE_KEY, rememberMe ? 'local' : 'session');
    } else {
      localStorage.removeItem(STORAGE_TYPE_KEY);
    }
    this.notifySubscribers(token);
  }

  clearToken(): void {
    this.token = null;
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(STORAGE_TYPE_KEY);
    this.notifySubscribers(null);
  }

  isTokenExpired(): boolean {
    if (!this.token) return true;

    try {
      const payload = JSON.parse(atob(this.token.split('.')[1]));
      const exp = payload.exp * 1000;
      return Date.now() >= exp;
    } catch {
      return true;
    }
  }

  isTokenExpiringSoon(thresholdMs: number = 60000): boolean {
    if (!this.token) return true;

    try {
      const payload = JSON.parse(atob(this.token.split('.')[1]));
      const exp = payload.exp * 1000;
      return Date.now() >= exp - thresholdMs;
    } catch {
      return true;
    }
  }

  subscribeToRefresh(callback: TokenRefreshSubscriber): () => void {
    this.subscribers.push(callback);
    return () => {
      this.subscribers = this.subscribers.filter((sub) => sub !== callback);
    };
  }

  private notifySubscribers(token: JWTToken | null): void {
    this.subscribers.forEach((callback) => callback(token));
  }

  async waitForRefresh(): Promise<JWTToken | null> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }
    return this.token;
  }

  setRefreshPromise(promise: Promise<JWTToken | null>): void {
    this.refreshPromise = promise;
    promise.finally(() => {
      this.refreshPromise = null;
    });
  }

  isRefreshing(): boolean {
    return this.refreshPromise !== null;
  }
}

export const tokenManager = TokenManager.getInstance();
